import { useRef, type ReactNode, type MouseEvent } from "react";
import { useReducedMotion } from "framer-motion";

interface Props {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

/**
 * Magnetic CTA wrapper — translates the inner element 2–4px toward the cursor
 * within a 60px radius. Fully disabled under prefers-reduced-motion.
 */
export const MagneticButton = ({ children, className, onClick, ariaLabel }: Props) => {
  const wrapperRef = useRef<HTMLButtonElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  const handleMove = (e: MouseEvent<HTMLButtonElement>) => {
    if (prefersReducedMotion || !innerRef.current || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.18;
    const dy = (e.clientY - cy) * 0.18;
    const max = 4;
    innerRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
  };

  const handleLeave = () => {
    if (innerRef.current) innerRef.current.style.transform = "translate(0, 0)";
  };

  return (
    <button
      ref={wrapperRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      aria-label={ariaLabel}
      className={className}
      type="button"
    >
      <span
        ref={innerRef}
        className="inline-flex items-center justify-center gap-2"
        style={{ transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        {children}
      </span>
    </button>
  );
};
